import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from './auth-context';
import { useUserSessionData } from './user-session-context';
import { authClient } from './auth-client';
import { fetchWithAuth } from '@/lib/auth/fetch-with-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/platform/ui/card';
import { Button } from '@/platform/ui/button';
import { Alert, AlertDescription } from '@/platform/ui/alert';
import { PendingApprovalScreen } from './pending-approval-screen';
import { Mail, Loader as Loader2, CircleCheck as CheckCircle2, Circle as XCircle, Building2 } from 'lucide-react';

type GuardState =
  | { status: 'loading' }
  | { status: 'ready' }
  | { status: 'unauthenticated' }
  | { status: 'needs_verification'; email: string }
  | { status: 'pending_approval'; email: string }
  | { status: 'needs_company_setup' };

const ALLOWED_PATHS = ['/auth', '/onboarding'];

export function RequireDashboardAuth({ children }: { children: React.ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const { isParticipantOnly, loading: sessionLoading } = useUserSessionData();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [guardState, setGuardState] = useState<GuardState>({ status: 'loading' });
  const lastCheckedTokenRef = useRef<string | null>(null);

  const isSurveyParticipantFlow = !!searchParams.get('survey_token');
  const isAssessmentRoute = /^\/products\/\w+\/assessment\//.test(pathname);

  const runChecks = useCallback(async () => {
    if (!user || !session?.access_token) {
      setGuardState({ status: 'unauthenticated' });
      return;
    }

    if (ALLOWED_PATHS.some((p) => pathname.startsWith(p))) {
      setGuardState({ status: 'ready' });
      return;
    }

    if (isSurveyParticipantFlow && isAssessmentRoute) {
      setGuardState({ status: 'ready' });
      lastCheckedTokenRef.current = session.access_token;
      return;
    }

    if (session.access_token === lastCheckedTokenRef.current) {
      setGuardState((prev) => prev.status === 'loading' ? { status: 'ready' } : prev);
      return;
    }

    try {
      const [verificationRes] = await Promise.all([
        fetchWithAuth('/api/auth/check-verification', session),
      ]);

      if (!verificationRes.ok) {
        // A failed check never lets a pending person through; it only fails open for everyone else.
        setGuardState((prev) => (prev.status === 'pending_approval' ? prev : { status: 'ready' }));
        return;
      }

      const verificationData = await verificationRes.json();

      if (verificationData?.pendingApproval) {
        setGuardState({
          status: 'pending_approval',
          email: user.email || '',
        });
        lastCheckedTokenRef.current = session.access_token;
        return;
      }

      const needsVerification =
        verificationData &&
        (!verificationData.emailVerified || verificationData.status !== 'active');

      if (needsVerification) {
        setGuardState({
          status: 'needs_verification',
          email: user.email || '',
        });
        lastCheckedTokenRef.current = session.access_token;
        return;
      }

      setGuardState({ status: 'ready' });
      lastCheckedTokenRef.current = session.access_token;
    } catch (err) {
      console.error('[RequireDashboardAuth] Error during checks:', err);
      setGuardState((prev) => (prev.status === 'pending_approval' ? prev : { status: 'ready' }));
    }
  }, [user, session?.access_token, pathname, isSurveyParticipantFlow, isAssessmentRoute]);

  // Look at the account status again, ignoring the cached result (used by the pending approval screen).
  const recheck = useCallback(async () => {
    lastCheckedTokenRef.current = null;
    await runChecks();
  }, [runChecks]);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      if (isSurveyParticipantFlow && isAssessmentRoute) {
        setGuardState({ status: 'ready' });
        return;
      }
      setGuardState({ status: 'unauthenticated' });
      navigate('/auth/login');
      return;
    }

    if (sessionLoading) return;

    if (isParticipantOnly && !pathname.startsWith('/participant') && !pathname.startsWith('/survey')) {
      if (!(isSurveyParticipantFlow && isAssessmentRoute)) {
        navigate('/participant/dashboard');
        return;
      }
    }

    runChecks();
  }, [authLoading, sessionLoading, user?.id, isParticipantOnly, pathname, isSurveyParticipantFlow, isAssessmentRoute, runChecks, navigate]);

  if (guardState.status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (guardState.status === 'unauthenticated') {
    return null;
  }

  if (guardState.status === 'pending_approval') {
    return <PendingApprovalScreen email={guardState.email} onRecheck={recheck} />;
  }

  if (guardState.status === 'needs_verification') {
    return <VerificationScreen email={guardState.email} />;
  }

  if (guardState.status === 'needs_company_setup') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-4 bg-primary/10 rounded-full">
                <Building2 className="h-12 w-12 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl mb-2">Company Setup Required</CardTitle>
            <CardDescription className="text-base">
              Before accessing products, please set up your company profile.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pb-6">
            <Loader2 className="h-6 w-6 text-primary animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}

function VerificationScreen({ email }: { email: string }) {
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkVerification = useCallback(async (showFeedback = false) => {
    if (showFeedback) {
      setChecking(true);
      setCheckMessage(null);
    }

    try {
      await authClient.auth.refreshSession();
      const { data: { user } } = await authClient.auth.getUser();

      if (!user) {
        navigate('/auth/login');
        return;
      }

      if (user.email_confirmed_at) {
        if (showFeedback) {
          setCheckMessage({ type: 'success', text: 'Email verified! Redirecting...' });
          setTimeout(() => window.location.reload(), 1000);
        } else {
          window.location.reload();
        }
        return;
      }

      if (showFeedback) {
        setCheckMessage({
          type: 'error',
          text: 'Email not yet verified. Please check your inbox and click the verification link.',
        });
      }
    } catch (err) {
      if (showFeedback) {
        setCheckMessage({ type: 'error', text: 'Unable to check status. Please try again.' });
      }
    } finally {
      if (showFeedback) setChecking(false);
    }
  }, [navigate]);

  useEffect(() => {
    pollIntervalRef.current = setInterval(() => {
      checkVerification();
    }, 5000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [checkVerification]);

  const handleResend = async () => {
    setResending(true);
    setResendError('');
    setResendSuccess(false);
    try {
      const { error } = await authClient.auth.resend({ type: 'signup', email });
      if (error) setResendError(error.message);
      else setResendSuccess(true);
    } catch (err: any) {
      setResendError(err.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.auth.signOut();
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <Mail className="h-16 w-16 text-primary" />
          </div>
          <CardTitle>Check Your Email</CardTitle>
          <CardDescription>One more step to get started</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <p className="text-sm">We sent a verification link to:</p>
            <p className="font-medium text-primary">{email}</p>
            <p className="text-sm text-muted-foreground">Click the link in that email to verify your account.</p>
          </div>

          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Waiting for verification...</span>
          </div>

          {resendSuccess && (
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">New verification email sent! Check your inbox.</AlertDescription>
            </Alert>
          )}
          {resendError && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{resendError}</AlertDescription>
            </Alert>
          )}
          {checkMessage && (
            <Alert
              variant={checkMessage.type === 'success' ? 'default' : 'destructive'}
              className={checkMessage.type === 'success' ? 'border-green-200 bg-green-50' : ''}
            >
              <div className="flex items-center gap-2">
                {checkMessage.type === 'success' ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={checkMessage.type === 'success' ? 'text-green-700' : ''}>{checkMessage.text}</AlertDescription>
              </div>
            </Alert>
          )}

          <div className="border-t pt-4 space-y-2">
            <p className="text-xs text-muted-foreground text-center mb-3">
              Didn't receive the email? Check spam or request a new one.
            </p>
            <Button onClick={handleResend} disabled={resending || checking} variant="outline" className="w-full">
              {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Resend Verification Email
            </Button>
            <Button onClick={() => checkVerification(true)} variant="ghost" className="w-full" disabled={checking}>
              {checking && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Check Status Now
            </Button>
            <Button onClick={handleSignOut} variant="link" className="w-full text-muted-foreground" disabled={checking}>
              Sign out and use a different email
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function RequireAdminAuth({ children }: { children: React.ReactNode }) {
  const { user, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const lastCheckedTokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (authLoading) return;

    if (!user || !session?.access_token) {
      navigate('/auth/login');
      return;
    }

    if (session.access_token === lastCheckedTokenRef.current) {
      return;
    }

    const checkAdmin = async () => {
      try {
        const res = await fetchWithAuth('/api/auth/check-admin', session);

        if (!res.ok) {
          navigate('/auth/login');
          return;
        }

        const data = await res.json();
        if (!data.isAdmin) {
          navigate('/dashboard');
          return;
        }

        lastCheckedTokenRef.current = session.access_token;
        setReady(true);
      } catch {
        navigate('/dashboard');
      }
    };

    checkAdmin();
  }, [authLoading, user?.id, session?.access_token, navigate]);

  if (authLoading || !ready) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
